import { ComponentProps, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { Box, BoxProps, BoxType, Flex } from 'reflexbox';
import { rgba } from 'polished';

import { Link, Route } from '@treams/treams-routes';
import { Container as PlainContainer } from './Container';

const Sticky = styled(Box)`
	position: sticky;
	z-index: 1;
	top: 0px;
	background: white;
	flex-shrink: 0;
	flex-grow: 0;
`;

const NonSticky = styled(Box)`
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

const Container = styled(Flex)<{ selected?: boolean }>`
	font-size: 16px;
	height: 60px;
	align-items: center;
	flex-grow: 0;
	flex-shrink: 0;
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
	cursor: pointer;

	${(props) =>
		props.selected
			? `
		font-weight: bold;
		color: ${props.theme.colors.primary};
		border-bottom: 1px solid ${props.theme.colors.primary};
	`
			: `
		color: ${props.theme.colors.slateBlue};

		&:hover {
			color: black;
			border-bottom: 1px solid black;
		}
	`}
`;

const BorderedBar = styled(Box)`
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

export function Tabs(props: PropsWithChildren<BoxProps>) {
	return (
		<NonSticky>
			<Flex mb="-1px" {...(props as ComponentProps<BoxType>)}>
				{props.children}
			</Flex>
		</NonSticky>
	);
}

Tabs.Container = function TabsContainer(
	props: PropsWithChildren<BoxProps & { wide?: number | boolean }>,
) {
	return (
		<Sticky overflowX="auto">
			<BorderedBar>
				<PlainContainer wide={props.wide}>
					<Box
						maxWidth={[
							'100%',
							'100%',
							props.wide ? (typeof props.wide === 'number' ? props.wide : 'auto') : 800,
						]}
						marginX="auto"
					>
						<Flex ml={[-2, -2, -3]} mb="-1px" {...(props as ComponentProps<BoxType>)}>
							{props.children}
						</Flex>
					</Box>
				</PlainContainer>
			</BorderedBar>
		</Sticky>
	);
};

Tabs.Item = function TabItem(props: {
	title: string;
	selected?: boolean;
	path?: string[] | string;
	exact?: boolean;
	onClick?: () => void;
}) {
	return (
		<Box flexShrink={0} flexGrow={0} mx={[2, 2, 3]}>
			{props.path ? (
				<Link to={Array.isArray(props.path) ? props.path[0] : props.path}>
					<Route path={props.path} exact={props.exact ?? true}>
						{({ match }) => <Container selected={!!match}>{props.title}</Container>}
					</Route>
				</Link>
			) : (
				<Container selected={props.selected} onClick={props.onClick}>
					{props.title}
				</Container>
			)}
		</Box>
	);
};

const InlineTabsContainer = styled(Flex)`
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

export function InlineTabs(props: PropsWithChildren<ComponentProps<typeof Box>>) {
	return <InlineTabsContainer {...props} />;
}

Tabs.Inline = InlineTabs;

const InlineTabContainer = styled(Box)<{ selected?: boolean }>`
	border: 1px solid transparent;
	border-bottom: none;
	border-top-left-radius: 5px;
	border-top-right-radius: 5px;
	margin-bottom: -1px;
	margin-right: 2px;
	cursor: pointer;
	color: ${(props) => props.theme.colors.slateBlue};

	${(props) =>
		props.selected
			? `
        background: linear-gradient(
            ${props.theme.colors.white},
            ${props.theme.colors.white},
            ${props.theme.colors.whiteBlue}
        );
        border: 1px solid ${props.theme.colors.lightBlue};
        border-bottom: none;
        box-shadow: 0px -1px 1px ${rgba(props.theme.colors.slateBlue, 0.05)};
    `
			: `
        &:hover {
            background: ${rgba(props.theme.colors.lightBlue, 0.25)};
            
            &:active {
                background: ${rgba(props.theme.colors.lightBlue, 0.5)};
            }
        }
    `}
`;

export function InlineTab(
	props: PropsWithChildren<ComponentProps<typeof Box> & { selected?: boolean }>,
) {
	return <InlineTabContainer {...props} />;
}

InlineTabs.Item = InlineTab;
